import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Minus, Plus, ShoppingBag, Trash2 } from "lucide-react";
import { useCart } from "../context/CartContext";
import { useSeo } from "../lib/seo";

export default function CartPage() {
  useSeo({
    title: "Your Bag",
    description:
      "Review your Sparkwear bag — children's clothing, school bags, children's socks and more. Free UK delivery on orders over £50.",
  });
  const { items, setQty, removeItem, subtotal } = useCart();
  const delivery = subtotal >= 50 || subtotal === 0 ? 0 : 4.99;
  const total = subtotal + delivery;

  return (
    <main className="min-h-screen pb-24 pt-28 md:pt-36" data-testid="cart-page">
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <p className="text-xs font-bold uppercase tracking-[0.22em] text-blush-700">
            Sparkwear Basket
          </p>
          <h1 className="mt-3 font-display text-4xl font-semibold tracking-tight text-ink sm:text-5xl">
            Your Bag
          </h1>
        </motion.div>

        {items.length === 0 ? (
          <div
            data-testid="cart-empty-state"
            className="mt-12 flex flex-col items-center gap-4 rounded-[32px] border border-dashed border-lav-300 bg-white/60 py-20 text-center"
          >
            <ShoppingBag className="h-12 w-12 text-lav-500" />
            <p className="font-display text-2xl font-semibold text-ink">
              Your bag is empty
            </p>
            <p className="max-w-xs text-sm text-muted">
              Fill it with pastel-perfect, play-proof sparkles from our
              collection.
            </p>
            <Link
              to="/shop"
              data-testid="cart-empty-shop-button"
              className="mt-2 inline-flex items-center gap-2 rounded-full bg-ink px-7 py-3.5 font-display text-base font-semibold text-white transition-transform duration-300 hover:scale-[1.03]"
            >
              Start Shopping <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        ) : (
          <div className="mt-10 grid grid-cols-1 gap-10 lg:grid-cols-12">
            <div className="lg:col-span-7">
              <ul className="space-y-5">
                {items.map((it) => (
                  <li
                    key={`${it.productId}:${it.size}:${it.colour || ""}`}
                    data-testid={`cart-item-${it.productId}-${it.size}-${it.colour || "one"}`}
                    className="flex items-center gap-5 rounded-[28px] border border-lav-100 bg-white p-4"
                  >
                    <img
                      src={it.image}
                      alt={it.name}
                      className="h-24 w-20 shrink-0 rounded-2xl object-cover"
                    />
                    <div className="min-w-0 flex-1">
                      <p className="truncate font-display text-lg font-semibold text-ink">
                        {it.name}
                      </p>
                      <p className="mt-0.5 text-sm text-muted">
                        {it.colour ? `${it.colour} · ` : ""}Size {it.size} · £{it.price.toFixed(2)}
                      </p>
                      <div className="mt-3 flex items-center gap-3">
                        <div className="inline-flex items-center gap-1 rounded-full border border-lav-100 bg-cream p-1">
                          <button
                            data-testid={`cart-qty-minus-${it.productId}-${it.size}-${it.colour || "one"}`}
                            onClick={() => setQty(it.productId, it.size, it.qty - 1, it.colour || "")}
                            aria-label="Decrease quantity"
                            className="inline-flex h-7 w-7 items-center justify-center rounded-full transition-colors duration-200 hover:bg-lav-50"
                          >
                            <Minus className="h-3.5 w-3.5" />
                          </button>
                          <span
                            data-testid={`cart-qty-${it.productId}-${it.size}-${it.colour || "one"}`}
                            className="min-w-[1.75rem] text-center text-sm font-bold text-ink"
                          >
                            {it.qty}
                          </span>
                          <button
                            data-testid={`cart-qty-plus-${it.productId}-${it.size}-${it.colour || "one"}`}
                            onClick={() => setQty(it.productId, it.size, it.qty + 1, it.colour || "")}
                            aria-label="Increase quantity"
                            className="inline-flex h-7 w-7 items-center justify-center rounded-full transition-colors duration-200 hover:bg-lav-50"
                          >
                            <Plus className="h-3.5 w-3.5" />
                          </button>
                        </div>
                        <button
                          data-testid={`cart-remove-${it.productId}-${it.size}-${it.colour || "one"}`}
                          onClick={() => removeItem(it.productId, it.size, it.colour || "")}
                          aria-label={`Remove ${it.name} from bag`}
                          className="inline-flex items-center gap-1.5 text-xs font-semibold text-muted transition-colors duration-200 hover:text-blush-700"
                        >
                          <Trash2 className="h-4 w-4" /> Remove
                        </button>
                      </div>
                    </div>
                    <span className="font-display text-lg font-semibold text-ink">
                      £{(it.price * it.qty).toFixed(2)}
                    </span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="lg:col-span-5">
              <div
                data-testid="cart-summary-card"
                className="rounded-[32px] bg-gradient-to-br from-blush-100 via-lav-50 to-baby-50 p-7 md:p-8"
              >
                <h2 className="font-display text-2xl font-semibold text-ink">
                  Order Summary
                </h2>
                <dl className="mt-6 space-y-3 text-sm">
                  <div className="flex items-center justify-between">
                    <dt className="text-muted">Subtotal</dt>
                    <dd data-testid="cart-subtotal" className="font-semibold text-ink">
                      £{subtotal.toFixed(2)}
                    </dd>
                  </div>
                  <div className="flex items-center justify-between">
                    <dt className="text-muted">Delivery (UK · 3–5 days)</dt>
                    <dd data-testid="cart-delivery" className="font-semibold text-ink">
                      {delivery === 0 ? "FREE" : `£${delivery.toFixed(2)}`}
                    </dd>
                  </div>
                  <div className="flex items-center justify-between border-t border-ink/10 pt-3 text-base">
                    <dt className="font-display font-semibold text-ink">Total</dt>
                    <dd data-testid="cart-total" className="font-display text-2xl font-semibold text-ink">
                      £{total.toFixed(2)}
                    </dd>
                  </div>
                </dl>
                {delivery > 0 ? (
                  <p className="mt-3 text-xs text-muted">
                    Add £{(50 - subtotal).toFixed(2)} more for free UK delivery. We also deliver
                    worldwide — international prices are shown at checkout.
                  </p>
                ) : (
                  <p className="mt-3 text-xs text-muted" data-testid="cart-worldwide-hint">
                    You've got free UK delivery. We also deliver worldwide — international prices
                    are shown at checkout.
                  </p>
                )}
                <Link
                  to="/checkout"
                  data-testid="cart-checkout-button"
                  className="mt-6 flex w-full items-center justify-center gap-2 rounded-full bg-ink py-4 font-display text-base font-semibold text-white shadow-lg shadow-ink/15 transition-transform duration-300 hover:scale-[1.02]"
                >
                  Checkout <ArrowRight className="h-5 w-5" />
                </Link>
                <Link
                  to="/shop"
                  data-testid="cart-continue-link"
                  className="mt-3 flex w-full items-center justify-center rounded-full border-2 border-lav-300/70 bg-white/70 py-3.5 font-display text-base font-semibold text-ink transition-colors duration-300 hover:bg-white"
                >
                  Continue Shopping
                </Link>
              </div>
            </div>
          </div>
        )}
      </div>
    </main>
  );
}
